import {saveErrorDetails, updateProfileFkoApi, verifyRecaptcha} from '@/api/api';
import {useGetCachedUserExists} from '@/api/FKO/useGetCachedUserExists';
import {useGetTeamPublicInfo} from '@/api/User/useGetTeamPublicInfo';
import fcDallasLogo from '@/assets/img/fc-dallas-logo.png';
import MyTocaLogoWhiteSvg from '@/assets/img/my-toca-logo-white.svg?react';
import {logAnalyticsEvent} from '@/common/analytics-events';
import {getFkoInitialParamsLocalstorage, setFkoInitialParamsLocalstorage} from '@/common/fko-localstorage';
import {IS_TEST_ENVIRONMENT} from '@/common/isTestEnvironment';
import {useLoadScript} from '@/common/use-load-script.hook';
import {useGetSport} from '@/common/useGetSport';
import {EmailOtpModal} from '@/components/AccountVerification/EmailOtpModal';
import {PhoneVerificationModal} from '@/components/AccountVerification/PhoneVerificationModal';
import {VerificationChoiceModal} from '@/components/AccountVerification/VerificationChoiceModal';
import {FKOFormContainer} from '@/components/FKO/FKOFormContainer/FKOFormContainer';
import {FKOModal} from '@/components/FKO/FKOModal/FKOModal';
import {ErrorModal} from '@/components/FKO/PlayersListModals/FkoPlayersListModals';
import {DbatLogo} from '@/components/Forms/FkoLogos/DbatLogo';
import {TocaLogo} from '@/components/Forms/FkoLogos/TocaLogo';
import {FormHeading} from '@/components/Forms/FormHeading/FormHeading';
import {LoadingAnimation} from '@/components/Loader/LoadingAnimation';
import {getLocationBySiteId} from '@/constants/locations';
import {isLocationPermitted} from '@/feature-flags/fko-locations';
import {useAccountVerification, VerificationPayload} from '@/hooks/useAccountVerification';
import {useFormSubmissionIpAddress} from '@/hooks/useFormSubmissionIpAddress';
import {FkoRegistrationLandingForm, RegistrationFormTypes} from '@/pages/RegistrationLanding/RegistrationLandingForm';
import {useAppDispatch, useAppSelector} from '@/redux/reduxUtils';
import store from '@/redux/store';
import {SanitizedProfile} from '@/user/player-info.interface';
import {setCurrentAccountHolderProfileId, setCurrentAccountId} from '@/user/user-utils';
import {executeRecaptchaV3} from '@/utils/utils';
import {generateUuid} from '@/utils/uuid';
import {memo, useCallback, useEffect, useState} from 'react';
import {Helmet} from 'react-helmet';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {
  FKOPages,
  getPresetSessionTypeNamesBySport,
  parseFkoParamsFromSearchParams,
  recaptchaSiteKey,
  sendFkoHubspotData,
  sendFkoHubspotFormData,
} from '../../common/fko-utils';
import {fullStoryService} from '../../integrations/fullstory.service';
import {
  FkoInitialParams,
  FkoUserData,
  setFkoCurrentPage,
  setFkoInitialParams,
  setFkoPlayersArray,
  setFkoSiteId,
  setFkoUserData,
} from '../../redux/slices/fkoFormData';
import styles from './RegistrationLanding.module.scss';

export const RegistrationLanding = memo(function RegistrationLanding() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {sessionId} = useParams();
  const {teamId, locationId} = useAppSelector((state) => state.fkoFormDataReducer.initialParams);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {getCachedUserExists, getCachedUserExistsStatus} = useGetCachedUserExists();
  const ipAddress = useFormSubmissionIpAddress();
  const {data: teamPublicInfo, isLoading: isLoadingTeamInfo} = useGetTeamPublicInfo({teamId});
  const {
    isDbat,
    isPlayerAssessment,
    isClasses,
    isFreeTrialClasses,
    basePath,
    basePathWithoutSessionId,
    sport,
    fkoMethod,
    fkoAnalyticsPrefix,
    applyEligibilityCheck,
    isFreeBaselineSession,
  } = useGetSport();

  useLoadScript(`https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`);

  useEffect(() => {
    dispatch(setFkoCurrentPage(FKOPages.CONTACT_INFO));
    return () => {
      dispatch(setFkoCurrentPage(''));
    };
  }, [dispatch]);

  useEffect(() => {
    setCurrentAccountId(''); // I'm not sure anymore why this is here, but probably leave it for now.
    const urlParams = parseFkoParamsFromSearchParams(new URL(document.location.href).searchParams);

    const locationIdFromUrl = urlParams.locationId;
    let _location;
    if (locationIdFromUrl) {
      _location = getLocationBySiteId(locationIdFromUrl);
    }

    logAnalyticsEvent(`${fkoAnalyticsPrefix}_registration_landing_view`, {
      locationId: _location?.siteId,
      locationName: _location?.name,
    });

    // if we have a valid sessionId, we reload the correct initial params (this is probably a page refresh)
    if (sessionId) {
      const _fkoInitialParams = getFkoInitialParamsLocalstorage({paramsId: sessionId, sport}) ?? {...urlParams, sport};
      dispatch(setFkoInitialParams(_fkoInitialParams));
    } else {
      const sessionTypeNames = urlParams.sessionTypeNames ?? getPresetSessionTypeNamesBySport({sport});
      const fkoInitialParams: FkoInitialParams = {...urlParams, sessionTypeNames, sport};
      const {paramsId} = setFkoInitialParamsLocalstorage(fkoInitialParams);
      dispatch(setFkoInitialParams(fkoInitialParams));
      navigate(`/${paramsId}${basePathWithoutSessionId}`, {replace: true});
    }

    if (urlParams.embed === 'true') {
      document.body.classList.add('embedded');
    }

    // ---don't include `sessionId` in dependencies---
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePathWithoutSessionId, dispatch, fkoAnalyticsPrefix, navigate, sport]);

  const processUserExistsOrCreateInitialProfile = useCallback(
    async (payload: VerificationPayload) => {
      const {lookupResult, email, siteId, formData} = payload;
      const profiles = lookupResult?.data?.profiles ?? [];
      let accountHolder: SanitizedProfile | null =
        profiles.find((profile) => profile.accountHolder) ??
        profiles.find(
          (profile) =>
            profile.firstName.toLowerCase() === formData?.firstName.toLowerCase() &&
            profile.lastName.toLowerCase() === formData?.lastName.toLowerCase()
        ) ??
        (profiles.length ? profiles[0] : null);

      const _userData: FkoUserData = {
        dob: accountHolder?.dob ?? formData?.dob,
        email,
        firstName: accountHolder?.firstName ?? formData.firstName.trim(),
        lastName: accountHolder?.lastName ?? formData.lastName.trim(),
        location: accountHolder?.mboDetails?.siteId ?? siteId,
        phoneNumber:
          accountHolder?.phoneNumber && accountHolder?.phoneNumber !== '0000000000'
            ? accountHolder?.phoneNumber
            : formData?.phoneNumber,
        club: formData?.club,
        userId: lookupResult?.data?.userId,
      };

      dispatch(setFkoUserData(_userData));
      dispatch(
        setFkoPlayersArray(
          profiles.map((profile) => ({
            ...profile,
            playerId: profile.mboDetails?.clientId ?? generateUuid(),
            club: formData?.club ?? profile.club,
          }))
        )
      );

      if (!profiles.length || !accountHolder) {
        const newProfile = await updateProfileFkoApi({
          email,
          siteId,
          update: {
            dob: formData?.dob,
            email,
            firstName: (formData?.firstName ?? '').trim(),
            lastName: (formData?.lastName ?? '').trim(),
            phoneNumber: formData?.phoneNumber,
            tracking: {
              fko: store.getState().fkoFormDataReducer.initialParams || {},
            },
            club: formData?.club,
          },
        });
        accountHolder = newProfile;
      }

      if (accountHolder?._id) {
        setCurrentAccountHolderProfileId(accountHolder._id);
      }
    },
    [dispatch]
  );

  const accountVerification = useAccountVerification({
    onVerificationSuccess: async (payload) => {
      await processUserExistsOrCreateInitialProfile(payload);
      navigate(`${basePath}/players`);
    },
    getNavigatePath: () => `${basePath}/players`,
    getCachedUserExists,
  });

  const checkExistingUserEligibilityOrCreateInitialProfile = useCallback(
    async ({email, siteId, formData}: {email: string; siteId: string; formData: RegistrationFormTypes}) => {
      try {
        const statusResult = await getCachedUserExistsStatus({
          email,
        });

        const needsVerification = await accountVerification.checkAndHandleVerification(
          statusResult,
          email,
          formData,
          siteId,
          store.getState().fkoFormDataReducer.initialParams.sessionTypeNames,
          applyEligibilityCheck
        );
        if (needsVerification) {
          return;
        }

        // No verification needed - fetch full data (including eligibility)
        const lookupResult = await getCachedUserExists({
          email,
          siteId,
          sessionTypeNames: store.getState().fkoFormDataReducer.initialParams.sessionTypeNames,
          applyEligibilityCheck,
        });

        await processUserExistsOrCreateInitialProfile({lookupResult, email, siteId, formData});
        navigate(`${basePath}/players`);
      } catch (err: unknown) {
        saveErrorDetails({
          key: 'fko-registration-landing-checkUserExistsAndFkoEligibility',
          severity: 'high',
          message: 'Caught error in RegistrationLanding.checkUserExistsAndFkoEligibility',
          description: err instanceof Error ? err.message : String(err),
          context: 'fko',
          email,
          siteId,
          url: window.location.href,
          data: {
            formData,
            error: err,
          },
        });
        setErrorMessage(`Error checking user email`);
        setIsLoading(false);
      } finally {
        if (!accountVerification.pendingPhoneVerification && !accountVerification.pendingEmailVerification) {
          setIsLoading(false);
        }
      }
    },
    [
      accountVerification,
      applyEligibilityCheck,
      basePath,
      getCachedUserExists,
      getCachedUserExistsStatus,
      navigate,
      processUserExistsOrCreateInitialProfile,
    ]
  );

  const onSubmitHandler = useCallback(
    async (data: RegistrationFormTypes) => {
      setIsLoading(true);
      if (!IS_TEST_ENVIRONMENT && import.meta.env.VITE_IS_CAPTCHA_DISABLED !== 'true') {
        try {
          const recaptchaToken = await executeRecaptchaV3(recaptchaSiteKey);
          const recaptchaResult = await verifyRecaptcha(recaptchaToken);
          if (!recaptchaResult.success || recaptchaResult.score < 0.3) {
            setErrorMessage('reCAPTCHA challenge failed');
            setIsLoading(false);
            return;
          }
        } catch (err) {
          setErrorMessage('reCAPTCHA challenge failed');
          setIsLoading(false);
          return;
        }
      }

      dispatch(setFkoUserData(data));
      dispatch(setFkoSiteId(data.location));

      logAnalyticsEvent(`${fkoAnalyticsPrefix}_registration_landing_form_submitted`, {
        locationId: data.location, // 'locationId' is a custom dimension in GA
        locationName: getLocationBySiteId(data.location)?.name,
      });

      const initialParams = store.getState().fkoFormDataReducer.initialParams;

      fullStoryService.identify(data.email, {
        displayName: `${data.firstName} ${data.lastName}`, // displayName and email are used in FullStory to label data
        email: data.email,
        locationId: data.location,
        locationName: getLocationBySiteId(data.location)?.name,
        firstName: data.firstName,
        lastName: data.lastName,
      });

      const sanitizedPath = basePathWithoutSessionId.startsWith('/')
        ? basePathWithoutSessionId
        : `/${basePathWithoutSessionId}`;
      const sanitizedFormUrl = typeof window !== 'undefined' ? `${window.location.origin}${sanitizedPath}` : undefined;

      const baseParams = {
        alternateProfileData: data,
        initialParams,
        locationId: data.location,
        fkoMethod,
      };

      if (isPlayerAssessment) {
        sendFkoHubspotData({
          ...baseParams,
          isPlayerAssessment: true,
        });
      } else {
        try {
          await sendFkoHubspotFormData({
            ...baseParams,
            formSubmissionUrl: sanitizedFormUrl,
            ipAddress,
          });
        } catch (error) {
          console.error('Error sending HubSpot data:', error);
        }
      }

      await checkExistingUserEligibilityOrCreateInitialProfile({
        email: data.email,
        siteId: data.location,
        formData: data,
      });
    },
    [
      fkoMethod,
      basePath,
      ipAddress,
      checkExistingUserEligibilityOrCreateInitialProfile,
      fkoAnalyticsPrefix,
      dispatch,
      basePathWithoutSessionId,
    ]
  );

  let locationIsBlocked = false;
  if (locationId) {
    const location = getLocationBySiteId(locationId);
    if (!location) {
      locationIsBlocked = true;
    } else {
      locationIsBlocked = !isLocationPermitted({locationId, isDbat, isPlayerAssessment, isClasses, isFreeTrialClasses});
    }
  }

  if (locationIsBlocked) {
    return (
      <div className={styles.restrictionOverlay}>
        <div className={styles.restrictionText}>
          {isDbat ? <DbatLogo withBackground={true} /> : <TocaLogo />}
          <h1>Your location is not yet available</h1>
          <h2>Please check back soon!</h2>
          <p>
            <Link to={`${basePathWithoutSessionId}/restart`} className="link-btn">
              Sign out
            </Link>
          </p>
        </div>
      </div>
    );
  }

  let pageTitle = 'MyTOCA | Free Baseline Session';
  let formHeading = (
    <FormHeading
      heading="Claim Your Free Intro Session"
      subheading={
        <div className="px-2">
          <p>
            Get your player on the schedule in under 2 minutes. No payment required; just show up ready to improve your
            player&apos;s game.
          </p>
          {isFreeBaselineSession && (
            <p className="mt-2 text-sm text-gray-300">Best for club and select players, ages 7 to 18+.</p>
          )}
        </div>
      }
    />
  );

  if (isDbat) {
    pageTitle = 'D-Bat | Free Private Skills Assessment';
  }

  if (isPlayerAssessment) {
    const isPlayerAssessmentLoading = !!teamId && isLoadingTeamInfo;
    // ---TEMP ensure we don't break existing FCD functionality---
    const isFCDallas = locationId === '5733380';
    const logoUrl = isFCDallas ? fcDallasLogo : teamPublicInfo?.logoUrl; // TODO find a better placeholder?
    const teamName = isFCDallas ? 'FC Dallas' : teamPublicInfo?.teamName ?? 'Player Assessment';
    const locationDetail = isFCDallas ? 'UMB Bank Performance Center' : ''; // TODO get param from somewhere?

    pageTitle = 'MyTOCA | Player Assessment';
    formHeading = (
      <div className="player-assessment-heading-section">
        <div className="flex justify-center h-[14px] mt-4">
          <MyTocaLogoWhiteSvg />
        </div>
        {isPlayerAssessmentLoading ? (
          <LoadingAnimation darkMode />
        ) : (
          <>
            {logoUrl ? (
              // only fade-in if the logo was loaded in (`teamId` enables the `teamPublicInfo` hook)
              <div className={`flex justify-center h-[100px] mt-8 ${teamId ? 'animate-fade-in' : ''}`}>
                <img
                  src={logoUrl}
                  alt={`${teamName} Logo`}
                  className="h-[100px] w-[100px] max-w-[100px] object-cover"
                />
              </div>
            ) : (
              <div className={teamId ? `h-[100px]` : `h-[40px]`} /> // prevents layout shift between loading states
            )}
            <h1 className="font-helvetica text-white text-center text-[26px] font-bold">{teamName}</h1>
            <h2 className="font-poppins text-white text-center text-base mt-4 mb-3 px-6">
              {`Please sign up and register for your player assessment` +
                (locationDetail ? ` at the ${locationDetail}` : ``)}
            </h2>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <body className="fko-registration-page" />
      </Helmet>

      <FKOModal
        isOpen={!!errorMessage}
        closeModal={() => setErrorMessage(undefined)}
        heading="Sorry! Something went wrong."
      >
        <ErrorModal />
      </FKOModal>

      {accountVerification.showVerifyChoice && accountVerification.showVerifyChoice.phoneNumber && (
        <VerificationChoiceModal
          email={accountVerification.showVerifyChoice.email}
          phoneNumber={accountVerification.showVerifyChoice.phoneNumber}
          isAccountCreation={accountVerification.showVerifyChoice.isAccountCreation}
          onChoosePhone={accountVerification.handleChoosePhone}
          onChooseEmail={accountVerification.handleChooseEmail}
        />
      )}

      {accountVerification.pendingEmailVerification && (
        <EmailOtpModal
          email={accountVerification.pendingEmailVerification.email}
          code={accountVerification.emailOtpCode}
          onCodeChange={accountVerification.setEmailOtpCode}
          error={accountVerification.emailOtpError}
          isSending={accountVerification.isSendingEmailOtp}
          isLoading={accountVerification.isLoading}
          resendSuccessMessage={accountVerification.resendSuccessMessage}
          resendCooldownSeconds={accountVerification.getResendCooldownSeconds()}
          onVerify={accountVerification.handleEmailVerificationComplete}
          onCancel={accountVerification.handleCancelEmailVerification}
          onResend={accountVerification.handleResendEmailCode}
        />
      )}

      {accountVerification.pendingPhoneVerification && (
        <PhoneVerificationModal
          email={accountVerification.pendingPhoneVerification.formData.email}
          phoneNumber={accountVerification.pendingPhoneVerification.phoneNumber}
          isNewAccount={accountVerification.pendingPhoneVerification.isNewAccount}
          onVerificationComplete={accountVerification.handlePhoneVerificationComplete}
          onSwitchToEmail={accountVerification.handleSwitchPhoneToEmail}
        />
      )}

      <FKOFormContainer restartLink={false}>
        {formHeading}
        <FkoRegistrationLandingForm
          errorMessage={errorMessage}
          onSubmitHandler={onSubmitHandler}
          getCachedUserExists={getCachedUserExists}
          isLoading={isLoading}
        />
      </FKOFormContainer>
    </>
  );
});
